import { AccessDocument } from '@bpa/core';
import { ROLES_WITH_ACCESS_TO_ADMIN_UI } from '@constants/accesses';
import { identityService } from '@services/identity.service';
import { getAuthSession } from '@stores/authStore';
import { notify } from '@stores/notifyStore';
import { envWindowReplaceCallback } from '@utils/envWindowReplaceCallback';

const ROOT_TENANT_ID = import.meta.env.VITE_ROOT_ORG_ID;

/*
 !Important Impersonation Details from the API:
  * you cannot impersonate yourself
  * you cannot impersonate someone that has a ys-admin role
  * you cannot impersonate if you are already impersonating
  * you cannot impersonate someone that doesn’t have an access record to match your current access
  * your currentAccess must be admin or staff to impersonate.
*/

export const useImpersonation = () => {
  const beginImpersonation = async (userId: string) => {
    try {
      const response = await identityService.beginImpersonation(userId);

      if (response) {
        envWindowReplaceCallback(`https://brightpath.youscience.com/home`);
      }
    } catch (error) {
      notify({ message: 'An error occurred while attempting to impersonate this user', severity: 'error' });
    }
  };

  const redirectUser = (userId?: string) => {
    const url = `${window.location.protocol}//${window.location.host}/users/`;

    if (userId) url.concat(userId);

    window.location.assign(`${window.location.protocol}//${window.location.host}/users/${userId}`);
  };

  const removeImpersonation = async () => {
    const authSession = getAuthSession();

    try {
      const response = await identityService.removeImpersonation();

      if (response) {
        redirectUser(authSession?.userData?.userId);
      }
    } catch (error) {
      notify({ message: 'There was an error ending your impersonation', severity: 'error' });
    }
  };

  const checkYsAdminImpersonating = () => {
    const authSession = getAuthSession();
    const isYsAdminImpersonating = authSession?.impersonatedBy?.access[0]?.tenant?.tenantId === ROOT_TENANT_ID;
    const isValidAdminUiImpersonationRole = ROLES_WITH_ACCESS_TO_ADMIN_UI.includes(
      authSession?.currentAccess?.role ?? 'Learner',
    );

    const isYsAdminImpersonatingAllowedAdminUiRole = isYsAdminImpersonating && isValidAdminUiImpersonationRole;

    // previous implementation was to redirect to /users if the user was being impersonated
    const noCurrentPathname = window.location.pathname === '/' || window.location.pathname === '';
    // eslint-disable-next-line no-nested-ternary
    const routePath = isYsAdminImpersonatingAllowedAdminUiRole
      ? noCurrentPathname
        ? '/users'
        : `${window.location.pathname}`
      : '/impersonating';

    return { isYsAdminImpersonating, routePath };
  };

  const canImpersonate = (userId: string, impersonateeAssociatedOrgs?: AccessDocument[]) => {
    const authSession = getAuthSession();
    const currentRole = authSession?.currentAccess?.role ?? 'Learner';
    const isYsAdmin = authSession?.currentAccess?.tenantId === ROOT_TENANT_ID && currentRole === 'Admin';
    const validatedAccesses = impersonateeAssociatedOrgs?.filter((access) => access.user.userId === userId) ?? [];

    if (userId === authSession?.userData?.userId) return false;
    if (!['Admin', 'Staff'].includes(currentRole)) return false;
    if (impersonateeAssociatedOrgs === undefined || (validatedAccesses.length === 0 && !isYsAdmin)) return false;

    const { isYsAdminImpersonating } = checkYsAdminImpersonating();

    const impersonateeHasRootAdminAccess =
      validatedAccesses.some((x) => x.tenant.tenantId === ROOT_TENANT_ID && x.tenant.permission?.role === 'Admin') ??
      false;
    const impersonateeHasForbiddenAccessLevel =
      currentRole !== 'Staff'
        ? false
        : validatedAccesses.some((x) => ['Admin', 'Staff', 'Proctor'].includes(x.tenant.permission?.role ?? 'Learner'));

    return !impersonateeHasRootAdminAccess && !isYsAdminImpersonating && !impersonateeHasForbiddenAccessLevel;
  };

  return {
    beginImpersonation,
    canImpersonate,
    removeImpersonation,
    checkYsAdminImpersonating,
  };
};
